/**
 * PlayerCard — per-player row: active highlight, score estimate, nature pips.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 * Adapted for multiplayer: includes seat index as data attribute for E2E assertions.
 */

import type { PlayerState } from "@cloudnest/cascadia-engine";

export interface PlayerCardProps {
  readonly player: PlayerState;
  readonly isActive: boolean;
  readonly seatIndex: number;
  readonly isMyPlayer?: boolean;
}

export function PlayerCard({
  player,
  isActive,
  seatIndex,
  isMyPlayer = false,
}: PlayerCardProps) {
  return (
    <div
      data-testid={`cascadia-player-card-${seatIndex}`}
      data-seat={seatIndex}
      data-active={isActive ? "true" : "false"}
      style={{
        padding: "12px 16px",
        borderRadius: 8,
        border: isActive
          ? "2px solid var(--cn-cascadia-selection)"
          : "1px solid var(--cn-cascadia-tile-outline)",
        background: isActive
          ? "var(--cn-cascadia-phase-bg)"
          : "var(--cn-cascadia-tile-bg)",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        transition: "border-color 0.15s",
      }}
      aria-current={isActive ? "true" : undefined}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          data-testid={`cascadia-player-name-${seatIndex}`}
          style={{
            fontWeight: 600,
            fontSize: 14,
            color: "var(--cn-cascadia-label)",
          }}
        >
          {isActive ? "▶ " : ""}
          {player.displayName}
          {isMyPlayer ? " (You)" : ""}
        </span>
        <span
          data-testid={`cascadia-player-score-${seatIndex}`}
          style={{
            fontSize: 13,
            color: "var(--cn-cascadia-label)",
          }}
        >
          {player.scoreBreakdown?.total ?? "—"}p
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "var(--cn-cascadia-nature)",
          }}
        >
          Nature:
        </span>
        <span
          data-testid={`cascadia-player-nature-${seatIndex}`}
          style={{
            fontSize: 11,
            color: "var(--cn-cascadia-nature)",
            fontWeight: 600,
          }}
        >
          {Array.from({ length: player.natureTokens }, (_, i) => (
            <span key={i} aria-hidden="true">
              ●
            </span>
          ))}
          {player.natureTokens === 0 ? "0" : ""}
        </span>
      </div>
    </div>
  );
}
